import { create } from "zustand";
import { io, Socket } from "socket.io-client";
import useAuthStore from "./authStore";
import { chatService } from "../services/chat.service";
import { ChatMessage, ChatUser } from "../types/chat";

/**
 * Resolve the Socket.IO connection target.
 *
 * Jun 17 — TIMEOUT FIX. socket.io's io(url) treats any PATH in the URL as a
 * namespace. VITE_API_BASE_URL is the REST base and often carries a path
 * (e.g. http://localhost:3000/api) — axios appends route paths to it, so REST
 * works, but feeding the same value to io() makes the client connect to a
 * "/api" namespace the server never registered → the handshake never completes
 * → "Error: timeout". The socket must target the bare ORIGIN.
 *
 * Also fixes the previous malformed fallback "http:///localhost:3000" (three
 * slashes = empty host).
 */
function resolveSocketOrigin(): string {
  const fallback = "http://localhost:3000";
  const raw = import.meta.env.VITE_API_BASE_URL || fallback;
  try {
    const u = new URL(raw);
    // strip any path/namespace — connect to scheme://host:port only
    return u.host ? u.origin : fallback;
  } catch {
    return fallback;
  }
}

/**
 * Append a message to a chat list WITHOUT duplicating it.
 *
 * The sender adds its own message optimistically in sendMessage(), and the
 * backend also broadcasts that same persisted message back over "newMessage".
 * Both copies share the same id, so a naive append shows the message twice and
 * trips React's duplicate-key warning. Dedupe by id (also guards against
 * reconnect replays). If a message somehow lacks an id, it's appended as-is.
 */
function appendMessage(
  list: ChatMessage[] | undefined,
  message: ChatMessage,
): ChatMessage[] {
  const existing = list || [];
  const id = (message as { id?: string })?.id;
  if (id && existing.some((m) => (m as { id?: string }).id === id)) {
    return existing;
  }
  return [...existing, message];
}

interface ChatState {
  socket: Socket | null;
  messages: Record<string, ChatMessage[]>;
  users: ChatUser[];
  activeChat: string | null;
  isConnected: boolean;
  error: string | null;
  isLoading: boolean;
  unreadCounts: Record<string, number>;

  // Actions
  initializeSocket: (token: string) => void;
  disconnectSocket: () => void;
  sendMessage: (
    content: string,
    type: "direct" | "broadcast",
    recipientId?: string,
    attachments?: Array<{ type: "image" | "file"; url: string; name: string }>,
  ) => Promise<void>;
  updateUserStatus: (status: "online" | "offline" | "away") => Promise<void>;
  setActiveChat: (userId: string | null) => void;
  markMessagesAsRead: (chatId: string) => void;
  loadMessages: (chatId: string) => Promise<void>;
  loadUsers: () => Promise<void>;
  incrementUnreadCount: (chatId: string) => void;
}

const useChatStore = create<ChatState>((set, get) => ({
  socket: null,
  messages: {},
  users: [],
  activeChat: null,
  isConnected: false,
  error: null,
  isLoading: false,
  unreadCounts: {},

  initializeSocket: (token: string) => {
    // Don't open a second socket if one already exists (store is a singleton;
    // initializeSocket may be called from more than one mount point).
    const existing = get().socket;
    if (existing) {
      if (!existing.connected) existing.connect();
      return;
    }

    const socket = io(resolveSocketOrigin(), {
      // Websocket only: behind a load balancer with several API servers,
      // long-polling would land on a different server each request.
      transports: ["websocket"],
      // Read the newest token at every (re)connect: access tokens are short-lived.
      auth: (cb) => cb({ token: useAuthStore.getState().token || token }),
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    socket.on("connect", () => {
      console.log("Socket connected");
      set({ isConnected: true, error: null });
      get().loadUsers();
    });

    socket.on("connect_error", (error) => {
      console.error("Socket connection error:", error);
      set({ isConnected: false, error: error.message });
    });

    socket.on("disconnect", () => {
      console.log("Socket disconnected");
      set({ isConnected: false });
    });

    socket.on("newMessage", (message: ChatMessage) => {
      const { activeChat } = get();
      const { user } = useAuthStore.getState();
      const isOwn = message.senderId === user?.id;

      // Determine the correct chat ID based on message type and participants
      let chatId: string;
      if (message.messageType === "broadcast") {
        chatId = "broadcast";
      } else if (isOwn) {
        // Current user is the sender → this is the echo of a message we sent;
        // the chat is the recipient.
        chatId = message.recipientId || "broadcast";
      } else if (message.recipientId === user?.id) {
        // Current user is the recipient → the chat is the sender.
        chatId = message.senderId;
      } else {
        // Not ours.
        return;
      }

      // Add message (deduped — own sends were already appended optimistically)
      set((state) => ({
        messages: {
          ...state.messages,
          [chatId]: appendMessage(state.messages[chatId], message),
        },
      }));

      // Only count as unread if it's from someone ELSE and not the open chat.
      if (!isOwn && chatId !== activeChat) {
        get().incrementUnreadCount(chatId);
      }
    });

    socket.on(
      "userStatusUpdate",
      (update: { userId: string; status: "online" | "offline" | "away" }) => {
        set((state) => ({
          users: state.users.map((user) =>
            user.userId === update.userId
              ? { ...user, status: update.status }
              : user,
          ),
        }));
      },
    );

    set({ socket });
  },

  disconnectSocket: () => {
    const { socket } = get();
    if (socket) {
      socket.disconnect();
      set({ socket: null, isConnected: false });
    }
  },

  sendMessage: async (
    content: string,
    type: "direct" | "broadcast",
    recipientId?: string,
    attachments?: Array<{ type: "image" | "file"; url: string; name: string }>,
  ) => {
    const { socket } = get();
    const { user } = useAuthStore.getState();

    if (!socket || !user) {
      set({ error: "Socket not connected or user not authenticated" });
      return;
    }

    try {
      const message = await chatService.sendMessage(
        content,
        type,
        recipientId,
        attachments,
      );

      // Determine the correct chat ID for the sent message
      const chatId =
        type === "direct" && recipientId ? recipientId : "broadcast";

      set((state) => ({
        messages: {
          ...state.messages,
          [chatId]: appendMessage(state.messages[chatId], message),
        },
      }));
    } catch (error) {
      console.error("Error sending message:", error);
      set({ error: "Failed to send message" });
    }
  },

  updateUserStatus: async (status: "online" | "offline" | "away") => {
    const { socket } = get();
    if (!socket) {
      set({ error: "Socket not connected" });
      return;
    }

    try {
      await chatService.updateStatus(status);
      // Server listens for "updateStatus" (see initializeSocketIO); the old
      // "status_update" event name was a no-op on the server.
      socket.emit("updateStatus", { status });
    } catch (error) {
      console.error("Error updating status:", error);
      set({ error: "Failed to update status" });
    }
  },

  setActiveChat: (userId: string | null) => {
    set({ activeChat: userId });
    if (userId) {
      get().markMessagesAsRead(userId);
      get().loadMessages(userId);
    }
  },

  markMessagesAsRead: (chatId: string) => {
    set((state) => ({
      unreadCounts: {
        ...state.unreadCounts,
        [chatId]: 0,
      },
    }));
  },

  loadMessages: async (chatId: string) => {
    set({ isLoading: true });
    try {
      const messages = await chatService.getMessages(chatId);
      set((state) => ({
        messages: {
          ...state.messages,
          [chatId]: Array.isArray(messages) ? messages : [],
        },
        isLoading: false,
      }));
    } catch (error) {
      console.error("Error loading messages:", error);
      set({ error: "Failed to load messages", isLoading: false });
    }
  },

  loadUsers: async () => {
    set({ isLoading: true });
    try {
      const users = await chatService.getUsers();
      set({
        users: Array.isArray(users)
          ? users.map((user) => ({
              ...user,
              status: user.status || "offline", // Ensure status has a default value
            }))
          : [],
        isLoading: false,
      });
    } catch (error) {
      console.error("Error loading users:", error);
      set({ error: "Failed to load users", isLoading: false });
    }
  },

  incrementUnreadCount: (chatId: string) => {
    set((state) => ({
      unreadCounts: {
        ...state.unreadCounts,
        [chatId]: (state.unreadCounts[chatId] || 0) + 1,
      },
    }));
  },
}));

export default useChatStore;
